// Sign In with Apple button — dual path.
//
// WEB (PWA at kinly.geminii-ai.com): Apple's web JS SDK (appleid.auth.js)
//   with usePopup:true. aud = Services ID `com.geminii.kinly.signin`.
// NATIVE iOS (TestFlight / App Store): @capgo/capacitor-social-login plugin
//   uses AuthenticationServices (ASAuthorizationAppleIDProvider). Required
//   for App Store Guideline 4.8 compliance. aud = bundle id `com.geminii.kinly`.
//
// Both paths POST { id_token, code, name, email } to
// /api/auth/social/apple/callback. Backend accepts BOTH audiences — see
// social_auth.py.
import React from "react";
import { api, formatApiError } from "@/lib/api";
import {
  isNativeIos,
  ensureSocialLoginInitialized,
  getSocialLogin,
  withLoginTimeout,
} from "@/lib/socialLogin";

const APPLE_SDK_URL =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const APPLE_CLIENT_ID = "com.geminii.kinly.signin";
const APPLE_REDIRECT_URI = "https://kinly.geminii-ai.com/auth/apple/callback";

function ensureAppleSdkLoaded() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.AppleID && window.AppleID.auth) return Promise.resolve();
  const existing = document.getElementById("kinly-apple-sdk");
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Apple SDK failed to load")));
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.id = "kinly-apple-sdk";
    s.src = APPLE_SDK_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Apple SDK failed to load"));
    document.head.appendChild(s);
  });
}

const APPLE_LOGO = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

async function postAppleResultToBackend({ idToken, code, name, email }) {
  const { data } = await api.post("/auth/social/apple/callback", {
    id_token: idToken,
    code: code || null,
    name: name || undefined,
    email: email || undefined,
  });
  localStorage.setItem(
    "kinly.tokens",
    JSON.stringify({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    })
  );
  window.history.replaceState({}, "", "/today");
  window.location.href = "/today";
}

export function AppleAuthButton({ disabled = false, label = "Continue with Apple" }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const native = isNativeIos();
  const inited = React.useRef(false);

  // Web-only: pre-load + init Apple SDK on mount. Native skips.
  React.useEffect(() => {
    if (native) return;
    let cancelled = false;
    (async () => {
      try {
        await ensureAppleSdkLoaded();
        if (cancelled || inited.current) return;
        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: "name email",
          redirectURI: APPLE_REDIRECT_URI,
          usePopup: true,
        });
        inited.current = true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Apple SDK init failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [native]);

  const onClickNative = async () => {
    try {
      await ensureSocialLoginInitialized();
      const SocialLogin = getSocialLogin();
      if (!SocialLogin) throw new Error("Native social login unavailable");
      const result = await withLoginTimeout(SocialLogin.login({
        provider: "apple",
        options: { scopes: ["email", "name"] },
      }));
      const r = result?.result || result || {};
      const idToken =
        r.idToken ||
        r.identityToken ||
        r.accessToken?.idToken ||
        null;
      const code =
        r.authorizationCode ||
        r.accessToken?.authorizationCode ||
        r.serverAuthCode ||
        null;
      const profile = r.profile || {};
      const name = [profile.givenName, profile.familyName]
        .filter(Boolean)
        .join(" ")
        .trim() || profile.name || undefined;
      const email = profile.email || r.email || undefined;
      if (!idToken) {
        console.error("Apple native login returned no id_token", result);
        throw new Error("Apple did not return an id_token");
      }
      await postAppleResultToBackend({ idToken, code, name, email });
    } catch (e) {
      const code = e?.error || e?.code || "";
      if (
        code === "user_cancelled" ||
        code === "popup_closed_by_user" ||
        code === "1001" /* ASAuthorizationError.canceled */ ||
        code === "USER_CANCELLED"
      ) {
        setBusy(false);
        return;
      }
      const detail = e?.response?.data?.detail;
      const msg = formatApiError(detail) || e?.message || JSON.stringify(e) || "Apple sign-in failed";
      console.error("[Apple native]", e);
      setError(msg);
      setBusy(false);
    }
  };

  const onClickWeb = async () => {
    try {
      await ensureAppleSdkLoaded();
      if (!inited.current) {
        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: "name email",
          redirectURI: APPLE_REDIRECT_URI,
          usePopup: true,
        });
        inited.current = true;
      }
      const res = await window.AppleID.auth.signIn();
      const idToken = res?.authorization?.id_token;
      const code = res?.authorization?.code || null;
      if (!idToken) throw new Error("Apple sign-in returned no id_token");
      const name = res?.user?.name
        ? `${res.user.name.firstName || ""} ${res.user.name.lastName || ""}`.trim()
        : undefined;
      const email = res?.user?.email || undefined;
      await postAppleResultToBackend({ idToken, code, name, email });
    } catch (e) {
      const code = e?.error || "";
      if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") {
        setBusy(false);
        return;
      }
      const detail = e?.response?.data?.detail;
      setError(formatApiError(detail) || e?.message || "Apple sign-in failed");
      setBusy(false);
    }
  };

  const onClick = async () => {
    if (busy) return;
    setError("");
    setBusy(true);
    if (native) await onClickNative();
    else await onClickWeb();
  };

  return (
    <>
      <button
        type="button"
        data-testid="signin-apple"
        onClick={onClick}
        disabled={disabled || busy}
        className="w-full h-14 rounded-2xl bg-black text-white font-medium tap flex items-center justify-center gap-2.5 disabled:opacity-60 hover:bg-black/90 transition-colors"
      >
        {APPLE_LOGO}
        <span className="text-[15px]">{busy ? "Signing in…" : label}</span>
      </button>
      {error && (
        <p
          data-testid="signin-apple-error"
          className="mt-2 text-xs text-destructive bg-destructive/10 rounded-lg px-2 py-1"
        >
          {error}
        </p>
      )}
    </>
  );
}
